import { Slot } from 'radix-ui';
import * as React from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { Icon, LoadingIcon } from './Icon';
import { VariantProps, tv } from './utils/cn';

/**
 * `tailwind-variants` style factory powering {@link Button} and
 * {@link LinkButton}. Exported for the rare case where a third-party
 * component owns its root element but exposes a `className` slot: a
 * headless trigger, a date-picker nav button, etc.
 *
 * Prefer {@link Button} and {@link LinkButton} for normal React usage. Reach
 * for `buttonStyles` only when a component's own styling API forces a raw
 * className string.
 *
 * @example
 * const { button } = buttonStyles({ variant: 'outline', size: 'sm' });
 * <SomeThirdPartyTrigger className={button()} />
 */
export const buttonStyles = tv({
  slots: {
    button:
      'group/button relative inline-flex shrink-0 cursor-pointer items-center justify-center border border-transparent bg-clip-padding font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 has-[[data-slot=button-content][data-icon-only]]:aspect-square has-[[data-slot=button-content][data-icon-only]]:px-0 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0',
    content:
      'inline-flex items-center justify-center whitespace-nowrap transition-opacity',
    spinner:
      'pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity',
  },
  variants: {
    variant: {
      default: {
        button:
          'bg-primary text-primary-foreground not-disabled:hover:bg-primary/80',
      },
      outline: {
        button:
          'border-border bg-background not-disabled:hover:bg-muted not-disabled:hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
      },
      secondary: {
        button:
          'bg-secondary text-secondary-foreground not-disabled:hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
      },
      ghost: {
        button:
          'not-disabled:hover:bg-muted not-disabled:hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
      },
      destructive: {
        button:
          'bg-destructive/10 text-destructive-foreground not-disabled:hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20',
      },
      link: {
        button: 'text-primary underline-offset-4 not-disabled:hover:underline',
      },
      brand: {
        button:
          'bg-bg-indigo-strong-primary text-text-indigo-strong not-disabled:hover:bg-bg-indigo-strong-secondary',
      },
      brandSecondary: {
        button:
          'bg-bg-indigo-subtle-primary text-text-indigo-subtle not-disabled:hover:bg-bg-indigo-subtle-secondary',
      },
    },
    size: {
      xs: {
        button:
          "h-7 rounded-lg px-2 text-xs [&_svg:not([class*='size-'])]:size-3",
        content: 'gap-1',
      },
      sm: {
        button:
          "h-8 rounded-lg px-3 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        content: 'gap-1.5',
      },
      md: {
        button:
          "h-10 rounded-lg px-4 text-sm [&_svg:not([class*='size-'])]:size-4",
        content: 'gap-2',
      },
      lg: {
        button:
          "h-12 rounded-lg px-6 text-md [&_svg:not([class*='size-'])]:size-5",
        content: 'gap-2',
      },
    },
    loading: {
      true: { content: 'opacity-0', spinner: 'opacity-100' },
    },
    disabled: {
      true: { button: 'cursor-not-allowed opacity-50' },
    },
    fullWidth: {
      true: { button: 'w-full' },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

type ButtonStyleProps = VariantProps<typeof buttonStyles> & {
  /** Visual variant. @default 'default' */
  variant?: VariantProps<typeof buttonStyles>['variant'];
  /** Height scale. @default 'md' */
  size?: VariantProps<typeof buttonStyles>['size'];
  /**
   * Renders an overlay spinner and auto-disables the button. The spinner is
   * mounted whenever `loading` is defined (including `false`) so toggling
   * between states animates smoothly.
   */
  loading?: VariantProps<typeof buttonStyles>['loading'];
  /** Disables the button. Also applied automatically when `loading` is true. */
  disabled?: VariantProps<typeof buttonStyles>['disabled'];
  /** Stretches to fill parent width. @default false */
  fullWidth?: VariantProps<typeof buttonStyles>['fullWidth'];
};

function isIconOnly(children: React.ReactNode): boolean {
  const array = React.Children.toArray(children);
  if (array.length !== 1) return false;
  const only = array[0];
  if (!React.isValidElement(only)) return false;
  return only.type === Icon || only.type === LoadingIcon || only.type === 'svg';
}

function CoreButtonContent({
  children,
  loading,
  size,
}: Pick<ButtonStyleProps, 'loading' | 'size'> & {
  children: React.ReactNode;
}) {
  const { content, spinner } = buttonStyles({ loading, size });
  return (
    <>
      <span
        data-slot="button-content"
        data-icon-only={isIconOnly(children) || undefined}
        className={content()}
      >
        {children}
      </span>
      {loading !== undefined && (
        <span data-slot="button-spinner" className={spinner()}>
          <LoadingIcon size="auto" />
        </span>
      )}
    </>
  );
}

function CoreButton({
  children,
  className,
  fullWidth,
  loading,
  size = 'md',
  variant = 'default',
  ...props
}: ButtonStyleProps &
  React.ComponentProps<typeof Slot.Root> & {
    children: React.ReactElement;
  }) {
  const { disabled } = props;
  const { button } = buttonStyles({
    disabled,
    fullWidth,
    loading,
    size,
    variant,
  });
  return (
    <Slot.Root
      {...props}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={button({ className })}
    >
      {children}
    </Slot.Root>
  );
}

/**
 * Props for the {@link Button} component.
 *
 * Combines the Button-specific style variants (`variant`, `size`, `loading`,
 * `disabled`, `fullWidth`) with standard `<button>` HTML attributes (`type`,
 * `onClick`, `ref`, `aria-*`, `data-*`, etc.), so anything you can put on a
 * native button also works here.
 */
export type ButtonProps = ButtonStyleProps &
  React.ComponentProps<'button'> & {
    /**
     * Fires when the user presses the button while disabled or loading
     * (e.g. blocked-interaction analytics). Wired to pointer-down: native
     * `disabled` suppresses click events, but browsers still dispatch
     * pointer events on disabled controls (verified Chromium/Firefox/WebKit).
     */
    onClickDisabled?: () => void;
  };

/**
 * Primary button component for actions.
 *
 * Content goes in children: pass a string, an `<Icon>`, or icon + text.
 * When the only child is an `<Icon>` (or raw `<svg>`), the button
 * automatically renders square. Use `size="auto"` on `<Icon>`
 * children so the icon matches the button's size.
 *
 * The `loading` prop renders an overlaid spinner and fades the content, while also
 * internally disabling the button. The spinner is mounted whenever `loading`
 * is defined (even when `false`) so toggling from `false` → `true` gets a
 * smooth transition. Passing `disabled` alongside `loading` is
 * redundant only when the two come from the _same_ boolean; if they come
 * from different conditions (e.g. `loading={isSubmitting} disabled={!isDirty}`),
 * both are legitimately needed and the button disables when either is true.
 *
 * For a button-styled link, use {@link LinkButton}. To apply button styling
 * to a non-Button element that you can't replace, use the underlying
 * {@link buttonStyles}.
 *
 * @example
 * // Plain text button, default variant (primary).
 * <Button onClick={save}>Save</Button>
 *
 * @example
 * // Icon + text; icon auto-sizes to match the button.
 * <Button variant="outline" size="sm" onClick={copy}>
 *   <Icon name="Copy" size="auto" />
 *   Copy
 * </Button>
 *
 * @example
 * // Icon-only: auto-detected, renders as a square.
 * <Button variant="ghost" aria-label="Close" onClick={close}>
 *   <Icon name="X" size="auto" />
 * </Button>
 *
 * @example
 * // Loading state driven by the same boolean that disables the button.
 * <Button loading={isSubmitting} onClick={submit}>
 *   Submit
 * </Button>
 */
export function Button({
  children,
  onClick,
  onClickDisabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const { disabled, loading, size } = props;
  const isDisabled = disabled || loading;
  const handleClick = isDisabled ? undefined : onClick;
  const handlePointerDown =
    isDisabled && onClickDisabled ? onClickDisabled : props.onPointerDown;

  return (
    <CoreButton
      {...props}
      disabled={isDisabled}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
    >
      <button type={type} disabled={isDisabled}>
        <CoreButtonContent loading={loading} size={size}>
          {children}
        </CoreButtonContent>
      </button>
    </CoreButton>
  );
}

type AnchorLinkProps = React.ComponentProps<'a'> & {
  href: string;
  to?: never;
};

type RouterLinkProps = LinkProps & {
  href?: never;
  to: string;
};

/**
 * Props for the {@link LinkButton} component.
 *
 * Discriminated on whether you pass `href` (external anchor) or `to` (router
 * navigation). Exactly one is required; the other branch narrows it to
 * `never` so TypeScript rejects passing both or neither.
 *
 * The anchor branch spreads native `<a>` attributes (aria-*, data-*, onClick,
 * className, ref, `target`, `rel`, etc.). The router branch spreads react-router
 * `Link` props (all the anchor attributes plus `replace`, `state`, and more).
 */
export type LinkButtonProps = ButtonStyleProps &
  (AnchorLinkProps | RouterLinkProps);

/**
 * Button-styled link. Use for any anchor or client-side navigation that
 * should visually read as a button.
 *
 * Pass `href` to render an external `<a>` (defaults to
 * `target="_blank"` + `rel="noreferrer"`), or `to` to render a react-router
 * `<Link>`. Content goes in children, same rules as {@link Button},
 * including icon-only auto-detection and `<Icon size="auto" />` sizing.
 *
 * When `disabled` (or `loading`) is true, `LinkButton` falls back to a
 * disabled `<button>` rather than a non-clickable anchor, so screen readers
 * and keyboard navigation treat it correctly.
 *
 * @example
 * // Internal navigation.
 * <LinkButton to="/patients" variant="outline">Patients</LinkButton>
 *
 * @example
 * // External link, icon + text.
 * <LinkButton href="https://docs.example.com" variant="ghost" size="sm">
 *   <Icon name="ExternalLink" size="auto" />
 *   Docs
 * </LinkButton>
 *
 * @example
 * // Icon-only nav link: auto square, aria-label for accessibility.
 * <LinkButton to={`/patients/${id}`} aria-label="Open patient">
 *   <Icon name="ChevronRight" size="auto" />
 * </LinkButton>
 */
export function LinkButton({
  children,
  href,
  rel = 'noreferrer',
  target = '_blank',
  to,
  ...props
}: LinkButtonProps) {
  const { disabled, loading, size } = props;

  const isDisabled = disabled || loading;

  if (isDisabled) {
    return (
      <CoreButton {...props} disabled={isDisabled}>
        <button disabled type="button">
          <CoreButtonContent loading={loading} size={size}>
            {children}
          </CoreButtonContent>
        </button>
      </CoreButton>
    );
  }

  return (
    <CoreButton {...props} disabled={isDisabled}>
      {to != null ? (
        <Link to={to}>
          <CoreButtonContent loading={loading} size={size}>
            {children}
          </CoreButtonContent>
        </Link>
      ) : (
        <a href={href} rel={rel} target={target}>
          <CoreButtonContent loading={loading} size={size}>
            {children}
          </CoreButtonContent>
        </a>
      )}
    </CoreButton>
  );
}
